<script setup lang="ts">
/**
 * VideoSection —— `.scu-widget` 壳 + 原生 `<video>`（ADR-0004，F05）。
 *
 * F07 Asset-level：`<video>` 绑 `@error` → `useAssetFallback` 切到
 * 封面图 + "视频暂不可用" 提示文案 + 隐藏控件（不再渲染 `<video>`），
 * `console.error` 保留可观测信号。`videoUrl` 缺失走同款提示分支。
 */
import { computed } from 'vue';

import SectionCard from './SectionCard.vue';
import type { SectionComponentProps } from './section-props';
import { copy } from '@/i18n/zh-CN';
import { hasText, posterFor } from './section-presenters';
import { useAssetFallback } from '@/composables/use-asset-fallback';

const props = defineProps<SectionComponentProps>();
const poster = computed(() => posterFor(props.section));
const { failed, handleError } = useAssetFallback(`video section="${props.section.id}"`);
const hasVideo = computed(() => hasText(props.section.videoUrl));
</script>

<template>
  <SectionCard
     :id="props.id"
     :type="props.section.type"
     :title="props.section.title"
    :summary="props.section.summary"
    :show-draft-badge="props.showDraftBadge"
  >
    <video
      v-if="hasVideo && !failed"
      :class="$style.video"
      :poster="poster"
      :src="props.section.videoUrl ?? undefined"
      controls
      preload="metadata"
      playsinline
      @error="handleError"
    />
    <div v-else-if="hasVideo && failed" :class="$style.failed" role="status" data-testid="video-failed">
      <img
        v-if="poster"
        :class="$style.poster"
        :src="poster"
        :alt="copy.fallback.asset.videoUnavailable"
      >
      <p :class="$style.unavailable">{{ copy.fallback.asset.videoUnavailable }}</p>
    </div>
    <p v-else :class="$style.unavailable" role="status">
      {{ copy.fallback.asset.videoUnavailable }}
    </p>
  </SectionCard>
</template>

<style module>
.video {
  width: 100%;
  display: block;
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-card);
  background: var(--color-bg-secondary);
}

.failed {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--space-2);
  border: 1px dashed var(--color-border);
  border-radius: var(--radius-md);
  padding: var(--space-3);
  background: var(--color-bg-secondary);
  text-align: center;
}

.poster {
  width: 100%;
  border-radius: var(--radius-md);
  object-fit: cover;
}

.unavailable {
  margin: 0;
  border: 1px dashed var(--color-border);
  border-radius: var(--radius-md);
  padding: var(--space-3);
  color: var(--color-text-muted);
  font-size: var(--font-size-base);
  text-align: center;
}
</style>
